/** Report settimanale generato dai dati: testo della mail, numeri della settimana, export xlsx. Bozza Outlook: in arrivo. */
import { App, Button, Card, Col, Input, Row, Table, Tooltip } from "antd";
import { CopyOutlined, DownloadOutlined, MailOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ANCORA } from "@qa/shared";
import type { ReportDto, SegnalazioneRigaDto } from "@qa/shared";
import { api, download } from "../../api/client";
import { fmtDataOra, fmtNum } from "../../lib/format";
import { Flag, Kpi, KpiRow, PageHead, Sev, Stato, Vuoto } from "../../components/ui";
import { Link } from "react-router-dom";

function Righe({ righe }: { righe: SegnalazioneRigaDto[] }) {
  const { t } = useTranslation();
  if (!righe.length) return <Vuoto titolo={t("redflag.report.nessuna")} />;
  return (
    <Table
      size="small"
      rowKey="id"
      pagination={false}
      showHeader={false}
      dataSource={righe}
      columns={[
        { key: "s", width: 90, render: (_, r) => <Sev s={r.severita} /> },
        {
          key: "c",
          render: (_, r) => (
            <>
              <Link to={`/redflag/segnalazioni/${r.id}`}>{r.cliente}</Link>
              {r.progetto ? <span className="muted"> · {r.progetto}</span> : null}
              <Flag f={r.flag} />
            </>
          ),
        },
        { key: "v", align: "right", render: (_, r) => <span className="num" data-sensitive>{r.valoreTesto ?? ""}</span> },
      ]}
    />
  );
}

export default function ReportPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [sp, setSp] = useSearchParams();
  const settimana = sp.get("settimana") ?? undefined;
  const q = useQuery({ queryKey: ["report", settimana], queryFn: () => api.get<ReportDto>("/redflag/report", { settimana }) });
  const copia = async (testo: string) => {
    try {
      await navigator.clipboard.writeText(testo);
      message.success(t("redflag.report.copiato"));
    } catch {
      message.error(t("errore.generico"));
    }
  };
  return (
    <Stato q={q} righe={12}>
      {(r) => (
        <>
          <PageHead
            crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoRedflag"), to: "/redflag" }, { label: t("menu.report") }]}
            title={t("redflag.report.titolo", { settimana: r.settimana })}
            sub={`${t("redflag.report.sottotitolo")} ${t("redflag.report.generato", { quando: fmtDataOra(r.generatoIl) })}`}
            extra={
              <>
                <Input
                  style={{ width: 130 }}
                  defaultValue={r.settimana}
                  aria-label={t("redflag.report.settimana")}
                  className="mono"
                  onPressEnter={(e) => {
                    const v = (e.target as HTMLInputElement).value.trim().toUpperCase();
                    if (/^\d{4}-W\d{2}$/.test(v)) setSp({ settimana: v });
                  }}
                />
                <Button icon={<DownloadOutlined />} onClick={() => download("/redflag/report/export.xlsx", { settimana: r.settimana })}>
                  {t("azioni.esportaXlsx")}
                </Button>
                <Button icon={<CopyOutlined />} onClick={() => void copia(r.testoMail)}>
                  {t("redflag.report.copia")}
                </Button>
                <Tooltip title={r.bozzaOutlook.nota}>
                  <Button type="primary" icon={<MailOutlined />} disabled>
                    {t("redflag.report.bozza")} · {t("comune.inArrivo")}
                  </Button>
                </Tooltip>
              </>
            }
          />
          <KpiRow>
            <Kpi label={t("redflag.report.aperti")} value={fmtNum(r.aperti.totale)} note={`${r.aperti.CRITICAL} CRITICAL · ${r.aperti.HIGH} HIGH · ${r.aperti.MEDIUM} MEDIUM · ${r.aperti.LOW} LOW`} />
            <Kpi label={t("redflag.report.ncAperte")} value={fmtNum(r.avanzamento.ncAperte)} />
            <Kpi label={t("redflag.report.ncSenzaOwner")} value={fmtNum(r.avanzamento.ncSenzaOwner)} />
            <Kpi label={t("redflag.report.azioniScadute")} value={fmtNum(r.avanzamento.azioniScadute)} note={t("redflag.report.chiuse") + ": " + fmtNum(r.avanzamento.chiuseSettimana)} />
            {r.avanzamento.claimAperti !== null && <Kpi label={t("redflag.report.claimAperti")} value={fmtNum(r.avanzamento.claimAperti)} />}
          </KpiRow>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} xl={14}>
              <Card size="small" title={t("redflag.report.testoMail")} data-tip={ANCORA.reportMail}>
                <pre className="mail" data-sensitive>{r.testoMail}</pre>
              </Card>
            </Col>
            <Col xs={24} xl={10}>
              <Card size="small" title={t("redflag.report.nuove")}>
                <Righe righe={r.nuove} />
              </Card>
              <Card size="small" title={t("redflag.report.aggiornate")} style={{ marginTop: 16 }}>
                <Righe righe={r.aggiornate} />
              </Card>
              <Card size="small" title={t("redflag.report.temi")} style={{ marginTop: 16 }}>
                {r.temi.length ? (
                  r.temi.map((x) => (
                    <p key={x.causa} style={{ margin: "0 0 6px" }}>
                      <b>{x.causa}</b> <span className="num">({x.n})</span>
                      <br />
                      <small className="muted">{x.clienti.join(", ")}</small>
                    </p>
                  ))
                ) : (
                  <Vuoto titolo={t("redflag.report.nessuna")} />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Stato>
  );
}
